import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Row, Col, Popover, OverlayTrigger } from 'react-bootstrap';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import get from 'lodash/get';

import { Select, Button } from 'sa-ui';
import { promisify } from 'app/actions';

import SimulatedCorrectionUploadAnswerCardModal from './SimulatedCorrectionUploadAnswerCardModal';
import SimulatedUploadConfirmationModal from './SimulatedUploadConfirmationModal';
import SimulatedUpdateAnswerSheetConfirmationModal from './SimulatedUpdateAnswerSheetConfirmationModal';
import SimulatedCorrectionTermsOfUseModal from './SimulatedCorrectionTermsOfUseModal';
import SimulatedCorrectionAnswerList from './SimulatedCorrectionAnswerList';
import SimulatedCorrectionEditModal from './SimulatedCorrectionEditModal';
import SimulatedCorrectionEditConfirmModal from './SimulatedCorrectionEditConfirmModal';
import SimulatedCorrectionAuditConfirmModal from './SimulatedCorrectionAuditConfirmModal';
import SimulatedCorrectionRemoveConfirmationModal from './SimulatedCorrectionRemoveConfirmationModal';
import FinishConfirmationModal from './SimulatedCorrectionFinishConfirmationModal';
import FinishedConfirmationModal from './SimulatedCorrectionFinishedConfirmationModal';
import LoaderRequestReportAccess from './LoaderRequestReportAccess';

import * as classStudentActions from '../../classes-students/classesStudentsActions';
import { getGroups } from '../simulatedActions';
import * as actions from './simulatedCorrectionActions';

import * as helpers from '../simulatedHelpers';

import messages from './simulatedCorrectionMessages';

import './SimulatedCorrectionContainer.scss';

function SimulatedCorrectionContainer(props) {
  const [modal, setModal] = useState({});
  const [answerParams, setAnswerParams] = useState();
  const [filter, setFilter] = useState({});
  const [student, setStudent] = useState();
  const [manualLoad, setManualLoad] = useState(false);
  const [singularFile, setSingularFile] = useState(false);
  const [filesUploaded, setFilesUploaded] = useState([]);
  const [error, setError] = useState([]);
  const [errCode, setErrCode] = useState([]);
  const [isReportRequested, setReportRequested] = useState(false);
  // SA-1853
  const [finalizeError, setFinalizeError] = useState(false);
  // End SA-1853

  //const [state, setstate] = useState({
  //  isReportRequested:fals
  //})

  useEffect(() => {
    const idCSchool = JSON.parse(localStorage.getItem('idCSchool'));
    const idESCourse = JSON.parse(localStorage.getItem('idESCourse'));
    setFilter({ ...filter, idCSchool, idESCourse });

    if (idESCourse) {
      props.getStudentAnswerList({
        idCSchool: filter.idCSchool,
        idESCourse: filter.idESCourse,
        idEvaluation: props.idEvaluation,
      });
    }
  }, [props.groups]);

  useEffect(() => {
    props.getGroups({ idEvaluation: props.idEvaluation });
    props.getEvaluationInformation({ idEvaluation: props.idEvaluation });
  }, [props.idEvaluation]);

  useEffect(() => {
    if (singularFile) {
      setUploadCardsModal(true);
    }
  }, [props.singularFile]);

  function goBack() {
    window.history.go(-1);
  }

  // SA-1853 - a functional update keeps async callers from writing back the `modal`
  // they captured on an older render, which used to reopen modals already closed.
  const toggleModal = (name, visible) => () => {
    setModal((current) => ({ ...current, [name]: visible }));
  };

  function handleChangeSchool(event) {
    setFilter({ ...filter, idCSchool: event.target.value, idESCourse: undefined });
    if (event.target.value !== '') {
      localStorage.setItem('idCSchool', JSON.stringify(event.target.value));
    } else {
      localStorage.removeItem('idCSchool');
    }
    localStorage.removeItem('idESCourse');
  }

  function handleChangeIdESCourse(event) {
    setFilter({ ...filter, idESCourse: event.target.value });
    props.getStudentAnswerList({
      idCSchool: filter.idCSchool,
      idESCourse: event.target.value,
      idEvaluation: props.idEvaluation,
    });
    if (event.target.value !== '') {
      localStorage.setItem('idESCourse', JSON.stringify(event.target.value));
    } else {
      localStorage.removeItem('idESCourse');
    }
  }

  function handleSeeStudent(student) {
    setStudent(student);
    toggleModal('edit', true)();
  }

  function handleAuditStudent(student) {
    setStudent(student);
    toggleModal('edit', true)();
  }

  function handleUploadStudent(student) {
    setManualLoad(true);
    setStudent(student);
    setSingularFile(true);
    toggleModal('termsOfUse', true)();
  }

  async function handleManualLoad() {
    try {
      await props.addAnswerSheet({
        ...filter,
        idEvaluation: props.idEvaluation,
        idStudent: student.idUser,
      });
    } finally {
      props.getStudentAnswerList({
        idCSchool: filter.idCSchool,
        idESCourse: filter.idESCourse,
        idEvaluation: props.idEvaluation,
      });
      setManualLoad(false);
      setModal({ ...modal, termsOfUse: false, edit: true });
    }
  }

  function handleRequestRemove(answerParams) {
    setAnswerParams(answerParams);
    toggleModal('removeConfirm', true)();
  }

  async function removeAnswerSheet() {
    await props.removeAnswerSheet(answerParams);
    props.getStudentAnswerList({
      idCSchool: filter.idCSchool,
      idESCourse: filter.idESCourse,
      idEvaluation: props.idEvaluation,
    });
    setModal({ edit: false, removeConfirm: false });
  }

  async function handleSave(answers, optionLanguage) {
    try {
      await props.updateAnswerSheet({
        idCSchool: filter.idCSchool,
        idESCourse: filter.idESCourse,
        idEvaluation: props.idEvaluation,
        idStudent: student.idUser,
        answers,
        optionLanguage: optionLanguage || null,
      });
      props.getStudentAnswerList({
        idCSchool: filter.idCSchool,
        idESCourse: filter.idESCourse,
        idEvaluation: props.idEvaluation,
      });
      setModal({ edit: false, editConfirm: true });
    } catch (err) {
      setModal({ updateAnswerSheetError: true });
    }
  }

  function handleContinueUpload() {
    props.getStudentAnswerList({
      idCSchool: filter.idCSchool,
      idESCourse: filter.idESCourse,
      idEvaluation: props.idEvaluation,
    });
    setModal({ uploadConfirmation: false });
  }

  // SA-1853
  // The user got no feedback after finishing: the confirmation modal was opened right
  // after re-fetching the evaluation, and the GET_EVALUATION_INFORMATION request resets
  // `forbidden` to undefined, so the container rendered null while it was in flight and
  // the modal never became visible. Refreshing is unnecessary here because closing the
  // confirmation reloads the page.
  async function handleFinishAndRequestReport() {
    setReportRequested(true);
    try {
      await props.finalizeEvaluation({
        idEvaluation: props.idEvaluation,
      });
      setModal((current) => ({
        ...current,
        finishConfirmation: false,
        finishedConfirmation: true,
      }));
    } catch (err) {
      setModal((current) => ({ ...current, finishConfirmation: false }));
      setFinalizeError(true);
    } finally {
      setReportRequested(false);
    }
  }
  // End SA-1853
  //{loading ? <img src={loadingImg} alt="Loading" style={{ width: 250 }}></img> : false}
  const schoolOptions = uniqBy(props.groups, (g) => g.idCSchool).map((g) => ({
    value: g.idCSchool,
    label: g.schoolName,
  }));

  const courseOptions = helpers.getCourseOptions(props.groups, filter.idCSchool, true);

  const evaluationStep = props.evaluation.evaluation.idStep;

  // SA-1853 - getEvaluationInformation.php only returns idStep '22' when the evaluation
  // already has a row in module_es_evaluations_steps_status for the answer_sheets step,
  // which is exactly what finalizeEvaluation.php inserts. It only changes the label: the
  // button stays active because new answer sheets can still arrive after a first
  // finalization, and the team has to be notified again when they do.
  const answerSheetsStepFinished = evaluationStep === '22';

  const finishingConfirmation = (
    <Col className="d-flex align-items-end flex-column">
      <FormattedMessage
        {...(answerSheetsStepFinished
          ? messages.finishedAndReportRequested
          : messages.finishAndRequestReports)}
      >
        {(txt) => (
          <Button type="button" className="mt-5" onClick={toggleModal('finishConfirmation', true)}>
            {txt}
          </Button>
        )}
      </FormattedMessage>
    </Col>
  );

  // const popover = (
  //   <Popover id="popover-needClickBefore">
  //     <Popover.Body>
  //       <FormattedMessage {...messages.needClickBefore}>
  //         {(txt) => <span className="popover-correction">{txt}</span>}
  //       </FormattedMessage>
  //     </Popover.Body>
  //   </Popover>
  // );

  // const button = (
  //   <FormattedMessage {...messages.loadCardsButton}>
  //     {(txt) => (
  //       <button
  //         type="button"
  //         className="btn btn-block btn-primary"
  //         onClick={toggleModal('termsOfUse', true)}
  //       >
  //         {txt}
  //       </button>
  //     )}
  //   </FormattedMessage>
  // );

  // const overlayButton = (
  //   <OverlayTrigger placement="right" overlay={popover}>
  //     <button type="button" className="btn btn-block btn-primary">
  //       <FormattedMessage {...messages.loadCardsButton} />
  //     </button>
  //   </OverlayTrigger>
  // );

  if (props.forbidden) {
    return <FormattedMessage {...messages.forbidden} />;
  }

  if (props.forbidden === undefined) {
    return null;
  }

  const dates = get(props.evaluation, 'evaluation.dates') || [];
  const isReportCompleted = dates.some((d) => d.component === 'application' && d.complete);
  return (
    <div>
      {isReportRequested ? (
        <LoaderRequestReportAccess show={true} />
      ) : (
        <LoaderRequestReportAccess show={false} />
      )}
      <FinishConfirmationModal
        show={modal.finishConfirmation}
        onClose={toggleModal('finishConfirmation', false)}
        // SA-1853 - handleFinishAndRequestReport owns closing this modal so the loader
        // stays on screen until the request settles.
        onAccept={handleFinishAndRequestReport}
      />
      <FinishedConfirmationModal
        evaluation={props.evaluation}
        idEvaluation={props.idEvaluation}
        show={modal.finishedConfirmation}
        onClose={() => {
          toggleModal('finishedConfirmation', false)();
          window.history.go(0);
        }}
      />
      {/* SA-1853 - without this the loader just disappears when finalizeEvaluation fails. */}
      <FinishedConfirmationModal
        error
        evaluation={props.evaluation}
        idEvaluation={props.idEvaluation}
        show={finalizeError}
        onClose={() => setFinalizeError(false)}
      />

      <SimulatedCorrectionAuditConfirmModal
        show={modal.auditConfirm}
        onClose={toggleModal('auditConfirm', false)}
        onContinue={() => alert('implementar continuar')}
      />
      <SimulatedCorrectionEditConfirmModal
        show={modal.editConfirm}
        error={props.updateAnswerSheet.error}
        onClose={toggleModal('editConfirm', false)}
      />
      <SimulatedCorrectionEditModal
        {...props}
        {...filter}
        student={student}
        show={modal.edit}
        onRequestRemove={handleRequestRemove}
        onSave={handleSave}
        onClose={() => {
          props.getStudentAnswerList({
            idCSchool: filter.idCSchool,
            idESCourse: filter.idESCourse,
            idEvaluation: props.idEvaluation,
          });
          toggleModal('edit', false)();
        }}
      />
      <SimulatedCorrectionRemoveConfirmationModal
        show={modal.removeConfirm}
        onClose={() => {
          props.getStudentAnswerList({
            idCSchool: filter.idCSchool,
            idESCourse: filter.idESCourse,
            idEvaluation: props.idEvaluation,
          });
          toggleModal('removeConfirm', false)();
        }}
        onConfirm={removeAnswerSheet}
      />
      <div className="row align-items-end">
        <div className="col-md-6">
          <a className="return" onClick={props.history.goBack}>
            <i className="ti-arrow-left"> </i> <FormattedMessage {...messages.back} />
          </a>
          {/* <FormattedMessage {...messages.title}>
            {(txt) => <h5 className="title">{txt}</h5>}
          </FormattedMessage> */}
          <h6 className="subtitle" style={{ marginTop: 15 }}>
            {props.evaluation.evaluation.levelSegment.map((l) => (
              <div>{l}</div>
            ))}
            <div>{props.contract.application}</div>
          </h6>
        </div>
      </div>
      <Row className="align-items-center">
        <Col lg={2}>
          <h6 className="id mb-0">
            <div>
              {props.evaluation.evaluation.catalogings.map((c) => c.abbreviation).join(' | ')}
            </div>
            <div>ID: {props.evaluation.evaluation.idString}</div>
          </h6>
        </Col>
        <Col lg={6}>
          <Select
            containerClassName="mt-0"
            options={schoolOptions}
            nullable
            onChange={handleChangeSchool}
            placeholder={props.intl.formatMessage(messages.school)}
            value={filter.idCSchool}
          />
        </Col>
        <Col lg={4}>
          <Select
            containerClassName="mt-0"
            disabled={!filter.idCSchool}
            nullable
            options={courseOptions}
            onChange={handleChangeIdESCourse}
            placeholder={props.intl.formatMessage(messages.class)}
            value={filter.idESCourse}
          />
        </Col>
      </Row>
      <Row className="justify-content-between">
        <Col>
          <FormattedMessage {...messages.correctionMethod}>
            {(txt) => <h3 className="title mt-5 mb-1">{txt}</h3>}
          </FormattedMessage>
          <FormattedMessage {...messages.choiceInfo}>{(txt) => <p>{txt}</p>}</FormattedMessage>
        </Col>
        {
          // buton for requesting reports.
        }
        {finishingConfirmation}
      </Row>

      <div className="row">
        <div className="col-md-4">
          <div className={classNames('card h-100 card-school pb-5 correction-card')}>
            <div className="card-body">
              <FormattedMessage {...messages.sendCardsSubtitle}>
                {(txt) => <div className="card-title text-danger">{txt}</div>}
              </FormattedMessage>
              <FormattedMessage {...messages.sendCardsInfo}>
                {(txt) => <p>{txt}</p>}
              </FormattedMessage>
              {!filter.idESCourse ? (
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Popover id="popover-needClickBefore">
                      <Popover.Body>
                        <span className="popover-correction">
                          <FormattedMessage {...messages.needClickBefore} />
                        </span>
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <button type="button" className="btn btn-block btn-primary">
                    <FormattedMessage {...messages.loadCardsButton} />
                  </button>
                </OverlayTrigger>
              ) : (
                <button
                  type="button"
                  className="btn btn-block btn-primary"
                  onClick={() => setModal({ ...modal, uploadCardsModal: true })}
                >
                  <FormattedMessage {...messages.loadCardsButton} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {props.students.length > 0 && (
        <Row className="mt-3">
          <Col md={12}>
            <SimulatedCorrectionAnswerList
              students={props.students}
              onSee={handleSeeStudent}
              onAudit={handleAuditStudent}
              // onUpload={() => {
              //   setSingularFile(true);
              //   setUploadCardsModal(true);
              // }}
              onUpload={handleUploadStudent}
              totalWarning={props.totalWarning}
            />
          </Col>
        </Row>
      )}
      <SimulatedCorrectionUploadAnswerCardModal
        show={modal.uploadCardsModal}
        initialStep={singularFile ? 2 : 1}
        idEvaluation={props.idEvaluation}
        idCSchool={filter.idCSchool}
        idESCourse={filter.idESCourse}
        students={props.students}
        allowManualSelection={props.allowManualSelection}
        onClose={() => {
          props.resetCorrection();
          setModal({ uploadCardsModal: false });
          setSingularFile(false);
          props.getStudentAnswerList({
            idCSchool: filter.idCSchool,
            idESCourse: filter.idESCourse,
            idEvaluation: props.idEvaluation,
          });
        }}
        onCompleteUpload={(files) => {
          setFilesUploaded(files);
          toggleModal('uploadConfirmation', true)();
        }}
        singularFile={singularFile}
        onError={(error) => setError(error)}
        onErrCode={(errCode) => setErrCode(errCode)}
      />
      {/* <SimulatedCorrectionUploadAnswerCardModalOld
        isOpen={uploadCardsModal}
        singularFile={singularFile}
        onContinueCallback={() => {
          setUploadConfirmation(true);
          props.getStudentAnswerList({
            idCSchool: filter.idCSchool,
            idESCourse: filter.idESCourse,
            idEvaluation: props.idEvaluation,
          });
        }}
        onUpload={file => {
          const formData = new FormData();
          formData.append('idEvaluation', props.idEvaluation);
          formData.append('idCSchool', filter.idCSchool);
          formData.append('idCourse', filter.idESCourse);
          formData.append('file', file);
          props.correctAnswerSheet(formData);
        }}
        isLoading={typeof props.isLoading === 'boolean' ? props.isLoading : false} // Por conta do Marcos ter feito o props.isLoading se tornar uma promise
        correctionInProgress={props.correctionInProgress}
        correctionFinalized={props.correctionFinalized}
        correctionGoNext={props.correctionGoNext}
        correctionsWithSuccess={props.correctionsWithSuccess}
        correctionsResponses={props.correctionsResponses}
        setCorrectionInProgress={props.setCorrectionInProgress}
        setCorrectionFinalized={props.setCorrectionFinalized}
        setCorrectionGoNext={props.setCorrectionGoNext}
        setCorrectionsWithSuccess={props.setCorrectionsWithSuccess}
        setCorrectionsResponses={props.setCorrectionsResponses}
        setLoading={props.setLoading}
        resetCorrection={props.resetCorrection}
        singular
      /> */}
      <SimulatedUploadConfirmationModal
        isOpen={modal.uploadConfirmation}
        singularFile={singularFile}
        onClose={() => {
          setModal({ ...modal, uploadConfirmation: false });
          setSingularFile(false);
        }}
        onContinue={() => {
          setModal({ ...modal, uploadConfirmation: false });
          setSingularFile(false);
        }}
        error={props.correctAnswerSheetState.error || error}
        errCode={props.correctAnswerSheetState.errCode || errCode}
        amount={filesUploaded.length}
      />
      <SimulatedUpdateAnswerSheetConfirmationModal
        isOpen={modal.updateAnswerSheetError}
        onClose={toggleModal('updateAnswerSheetError', false)}
        error={props.updateAnswerSheet.error}
      />
      <SimulatedCorrectionTermsOfUseModal
        show={modal.termsOfUse}
        manual={manualLoad}
        onClose={() => {
          setManualLoad(false);
          toggleModal('termsOfUse', false)();
        }}
        onManuallyLoad={handleManualLoad}
        onContinue={() => {
          setManualLoad(false);
          setModal({ termsOfUse: false, uploadCardsModal: true });
          props.setLoading(false);
        }}
      />
    </div>
  );
}

SimulatedCorrectionContainer.defaultProps = {
  students: [],
};
function mapStateToProps({ simulatedCorrection, simulated, classesStudents, app }, { match }) {
  return {
    ...simulatedCorrection,
    ...match.params,
    classesStudents,
    groups: simulated.groups,
    isLoading: simulated.isLoading,
    allowManualSelection: app.user ? app.user.simplificaAnswerSheetManualSelection == 1 : false,
  };
}

export default compose(
  injectIntl,
  withRouter,
  connect(
    mapStateToProps,
    promisify(
      {
        ...actions,
        ...classStudentActions,
        getGroups,
      },
      {
        include: ['updateAnswerSheet', 'removeAnswerSheet', 'addAnswerSheet', 'finalizeEvaluation'],
      },
    ),
  ),
)(SimulatedCorrectionContainer);
